/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useState } from 'react';
import { Field, useFormikContext, FieldArray, ErrorMessage, getIn } from 'formik';
import { iAttachments } from '@interfaces/interfaces';
import { Button, DropdownMenu, FilesUploadAccessible, LimitedTextArea, TextInput } from '@components';
import { WhoPreparedReport } from '@constants';
import { uploadFileAWS } from '@api/APIServices';
import classNames from 'classnames';
import TrashIcon from '../../images/trash.svg';
import GoodUpload from '../../images/GoodUpload.svg';
import styles from './FileUploadGroupAccessible.module.scss';

interface iProps {
    uploadFieldArrayName: string;
    whoPrepared: string;
    briefDescription: string;
    uploadAReport: string;
    totalFileLimit: number;
    fileSizeErrorField: string;
    fileUploadFailErrorField: string;
    UploadFileSectionError: string;
    fileSizelimit: string;
    hasPrepared?: boolean;
    hasDescribe?: boolean;
    formType: string;
}

const FileUploadGroupAccessible = ({ uploadFieldArrayName, uploadAReport, whoPrepared, briefDescription, totalFileLimit, fileSizeErrorField, fileUploadFailErrorField, UploadFileSectionError, fileSizelimit, hasPrepared = true, hasDescribe = true, formType }: iProps): ReactElement => {
    const { errors, touched, values }: { errors: any; touched: any; values: any } = useFormikContext();
    const formikContext = useFormikContext();
    const hasError = getIn(errors, UploadFileSectionError);
    const touchedError = getIn(touched, UploadFileSectionError);
    const descriptionHasError = getIn(errors, briefDescription);
    const fileSizeHasError = getIn(values, fileSizeErrorField);
    const fileUploadHasError = getIn(values, fileUploadFailErrorField);
    const fileTypes = ['DOC', 'DOCX', 'XLS', 'XLSX', 'csv', 'JPG', 'JPEG', 'PNG', 'PDF', 'txt'];
    const files = (values[uploadFieldArrayName] || []);
    const [currentFile, setCurrentFile] = useState<File>();
    const [uploadButtonDisable, setUploadButtonDisable] = useState(false);
    const resetFieldValues = () => {
      setCurrentFile(new File([''], ''));
      formikContext.setFieldValue(briefDescription, '');
      formikContext.setFieldValue(uploadAReport, '');
    };
    const handleChange = (file: File) => {
        if (file) {
          formikContext.setFieldValue(fileSizeErrorField, false);
          formikContext.setFieldValue(fileUploadFailErrorField, false);
          formikContext.setFieldValue(uploadAReport, file.name);
          setCurrentFile(file);
        }
    };
    const handFileSizeError = () => {
      formikContext.setFieldValue(fileSizeErrorField, true);
      formikContext.setFieldTouched(fileSizeErrorField);
      setCurrentFile(new File([''], ''));
    };
    const handleFileUploadFailError = () => {
      formikContext.setFieldValue(fileUploadFailErrorField, true);
      formikContext.setFieldTouched(fileUploadFailErrorField);
      setCurrentFile(new File([''], ''));
    };
    const uploadFileAWSOnClick = () => {
        const ob = values.OBLIGATION_NUMBER;
        if (currentFile && currentFile.size > 0) {
           setUploadButtonDisable(true);
           uploadFileAWS(currentFile, ob, formType).then((res) => {
            const resString = JSON.stringify(res);
            if (!resString.includes('Error')) {
              const uploadedAttachment = {
                FileName: currentFile.name,
                FileProvider: values[whoPrepared],
                FileDescription: values[briefDescription],
                RemoteFileName: res.toString(),
              };
              files.push(uploadedAttachment);
              formikContext.setFieldValue(uploadFieldArrayName, files);
            } else if (resString.includes('Error') && resString.includes('size')) {
              handFileSizeError();
            } else if (resString.includes('Unknown Error')) {
              handleFileUploadFailError();
            }
            resetFieldValues();
          }).catch((e) => {
            handFileSizeError();
            resetFieldValues();
            console.log('Error in upload');
          });
        }
      };
  useEffect(() => {
    if (values[uploadFieldArrayName].length >= totalFileLimit || descriptionHasError) {
      setUploadButtonDisable(true);
    } else {
      if (!descriptionHasError) {
        formikContext.setFieldTouched(briefDescription);
      }
      setUploadButtonDisable(false);
    }

    if (currentFile) {
      formikContext.setFieldTouched(fileSizeErrorField);
      formikContext.setFieldTouched(fileUploadFailErrorField);
    }
  }, [currentFile, values[uploadFieldArrayName].length, descriptionHasError]);
  return (
    <div>
      <FieldArray
        name={uploadFieldArrayName}
        render={(arrayHelpers) => (
          <div>
            {values[uploadFieldArrayName].map((_item: any, index: number) => (
            // eslint-disable-next-line react/no-array-index-key
              <div key={index}>
                <div className={styles.descriptionDiv}>
                  <img src={GoodUpload} alt="Cloud upload checked" />
                  <div className={styles.middleCol}>
                    <p>
                      <strong>{_item.FileName}</strong>
                    </p>
                    <div>
                      { hasPrepared && (
                      <span className={styles.uploadFileText}>
                        {`Prepared by ${_item.FileProvider}`}
                      </span>
                    )}
                      { hasDescribe && (
                      <span className={styles.uploadFileText}>
                        {_item.FileDescription}
                      </span>
                      )}
                    </div>
                  </div>
                  <button type="button" onClick={() => arrayHelpers.remove(index)} className={styles.removeDiv}>
                    <img src={TrashIcon} alt="trash icon" />
                  </button>
                </div>
              </div>
          ))}
          </div>
      )}
    />
      <Field name={UploadFileSectionError} hidden />
      <div className={classNames({
        [styles.fileUploadSection]: true,
        [styles.fileUploadSectionError]: hasError && touchedError,
      })}>
        <div className={styles.FileUploadform}>
          {formType === 'Review' ? (
            <>
              <div className={hasPrepared && hasDescribe ? styles.gridColHalf : ''}>
                <Field component={FilesUploadAccessible} errorFieldName={getIn(values, fileUploadFailErrorField) ? fileUploadFailErrorField : fileSizeErrorField} handFileSizeError={handFileSizeError} name={uploadAReport} title="Select a document" labelContent={currentFile?.name} fileTypes={fileTypes} handleChange={handleChange} fileSizelimit={fileSizelimit} isMandatory />
                { hasPrepared && (
                  <Field component={DropdownMenu} name={whoPrepared} title="Who prepared this document?" isMandatory displayText={WhoPreparedReport} defaultOption hidden />
                )}
              </div>
              <Field
                name={briefDescription}
                component={LimitedTextArea}
                title="Document description"
                limit={100}
                formType={formType}
                disabled={values[uploadFieldArrayName].length >= totalFileLimit}
              />
            </>
          )
        : (
          <div className={hasPrepared && hasDescribe ? styles.gridColHalf : styles.gridColHalfWithoutFields}>
            <Field component={FilesUploadAccessible} errorFieldName={getIn(values, fileUploadFailErrorField) ? fileUploadFailErrorField : fileSizeErrorField} handFileSizeError={handFileSizeError} name={uploadAReport} title="Select a document" labelContent={currentFile?.name} fileTypes={fileTypes} handleChange={handleChange} fileSizelimit={fileSizelimit} isMandatory />
            { hasPrepared && (
              <Field component={DropdownMenu} name={whoPrepared} title="Who prepared this document?" isMandatory displayText={WhoPreparedReport} defaultOption hidden />
            )}
          </div>
        )}
          <div className={hasPrepared && hasDescribe ? styles.gridColOneQuarter : styles.gridColTwoWithoutFields}>
            { hasDescribe && (
            <Field
              name={briefDescription}
              component={TextInput}
              title="Briefly describe this document"
              isMandatory
              />
            )}
            <Button
              color={uploadButtonDisable ? 'grey' : 'white'}
              size="small_medium"
              className={classNames({
                [styles.uploadButton]: true,
                [styles.uploadButtonDis]: uploadButtonDisable,
                [styles.uploadButtonError]: hasError && touchedError,
                [styles.uploadButtonDiscriptionError]: formType !== 'Review' ? descriptionHasError : null,
                [styles.uploadButtonFileSizeError]: !hasDescribe && !hasPrepared && fileSizeHasError && formType !== 'Review',
              })}
              onClick={uploadFileAWSOnClick}
              isDisabled={uploadButtonDisable}>
              Upload
            </Button>
          </div>
        </div>
      </div>
      <ErrorMessage className={styles.errorMsg} name={UploadFileSectionError} component="span" />
    </div>
  );
};

export default FileUploadGroupAccessible;
